import React, { useMemo, useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button, { ButtonWithLoadingState } from '../../components/Button';
import Modal, { ModalTitle } from '../../components/Modal';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UserType } from '../../graphql/generated/graphql';
import type { EntitySelectConfig } from '../../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../../search/entitySearch/CategorizedEntitySelect';
import UserRow from '../../user/UserRow';
import { useAllBots } from '../../user/useAllUsers';
import styles from './TransferPostOwnershipModal.module.scss';

const TRANSFER_POST_OWNERSHIP_MODAL_USER = gql(`
  fragment TransferPostOwnershipModal_user on User {
    id
    name
  }
`);

type NewOwner = React.ComponentProps<typeof UserRow>['user'];

interface TransferPostOwnershipModalProps {
  close: () => void;
  onConfirm: (newOwner: NewOwner) => Promise<void>;
  user: DocumentType<typeof TRANSFER_POST_OWNERSHIP_MODAL_USER>;
  deactivating: boolean;
}

const TransferPostOwnershipModal = ({
  close,
  onConfirm,
  user,
  deactivating,
}: TransferPostOwnershipModalProps) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const currentUser = useAppSelector((state) => state.session.user);
  const [isEditing, setIsEditing] = useState(false);
  const [newOwner, setNewOwner] = useState<NewOwner>(currentUser);
  const bot = useAllBots()[0];

  const config = useMemo<EntitySelectConfig>(
    () => ({
      user: {
        label: 'Pick a new owner',
        filter: (u) => ![newOwner.id, user.id, bot?.id].includes(u.id),
        sortBy: (a, b) => {
          if (a.type === UserType.Admin) {
            return -1;
          }
          if (b.type === UserType.Admin) {
            return 1;
          }
          return 0;
        },
      },
      new: {
        getGroup: bot
          ? () => ({
              label: 'transfer to slab bot',
              options: [
                {
                  id: bot.id,
                  label: bot.name,
                  name: bot.name ?? 'Slab Bot',
                  secondary: bot.email,
                  value: bot.id,
                  avatar: bot.avatar,
                  onSelect: () => {
                    setNewOwner(bot);
                    setIsEditing(false);
                  },
                },
              ],
            })
          : undefined,
      },
      order: ['user', 'new'],
    }),
    [bot, newOwner.id, user.id],
  );

  return (
    <Modal width={425}>
      <ModalTitle close={close}>
        {deactivating ? 'Deactivate user?' : 'Transfer post ownership'}{' '}
      </ModalTitle>
      {deactivating ? (
        <>
          Ownership of {user.name}&apos;s published posts must be transferred to
          another user.
        </>
      ) : (
        <>
          Ownership of {user.name}&apos;s published posts will be transferred to
          a new owner.
        </>
      )}
      <h5 className={styles.ownerHeading}>New owner</h5>
      <div className={styles.owner}>
        {isEditing ? (
          <CategorizedEntitySelect
            autoFocus
            isClearable={false}
            onChange={(value) => {
              if (value?.type === 'user' && value.id !== user.id) {
                setNewOwner(value.user);
              }
              setIsEditing(false);
            }}
            value={{ id: newOwner.id, type: 'user' }}
            config={config}
            maxOptionsToShow={6}
            showMoreResults={false}
          />
        ) : (
          <>
            <div className={styles.ownerCard}>
              <UserRow user={newOwner} />
            </div>
            <Button
              size="small"
              variant="secondary"
              onClick={() => setIsEditing(true)}
            >
              Change
            </Button>
          </>
        )}
      </div>
      <div className={styles.buttons}>
        {deactivating && (
          <Button onClick={close} size="large" variant="secondary">
            Never mind
          </Button>
        )}
        <ButtonWithLoadingState
          onClick={async () => {
            setIsConfirming(true);

            try {
              await onConfirm(newOwner);
              close();
            } finally {
              setIsConfirming(false);
            }
          }}
          size="large"
          variant="primary"
          loading={isConfirming}
        >
          {deactivating ? 'Confirm' : 'Transfer'}
        </ButtonWithLoadingState>
      </div>
    </Modal>
  );
};

export default TransferPostOwnershipModal;
